import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { DropGrid } from '@/components/drops/DropGrid'
import { CountryField } from '@/components/ui/CountryField'
import { Icon } from '@/components/ui/Icon'
import { COUNTRIES } from '@/data/countries'
import { useListings } from '@/hooks/useListings'
import { cn } from '@/lib/cn'
import { Search } from '@/lib/icons'
import type { ListingType } from '@/lib/types/listing'

const TYPE_FILTERS: { label: string; value?: ListingType }[] = [
  { label: 'All' },
  { label: 'Referral', value: 'referral' },
  { label: 'Discount', value: 'discount' },
]

function parseType(value: string | null): ListingType | undefined {
  return value === 'referral' || value === 'discount' ? value : undefined
}

function parseCountry(value: string | null): string | undefined {
  const code = value?.trim().toUpperCase()
  return code && COUNTRIES.some((item) => item.code === code) ? code : undefined
}

export function DiscoverPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const q = searchParams.get('q')?.trim() ?? ''
  const type = parseType(searchParams.get('type'))
  const country = parseCountry(searchParams.get('country'))
  const [draft, setDraft] = useState(q)
  const { listings, loading, error, usingSamples } = useListings({
    q: q || undefined,
    type,
    country,
  })

  useEffect(() => {
    setDraft(q)
  }, [q])

  const setParam = useCallback(
    (key: string, next?: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          if (next) {
            params.set(key, next)
          } else {
            params.delete(key)
          }
          return params
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = draft.trim()
      if (next !== q) {
        setParam('q', next || undefined)
      }
    }, 300)
    return () => {
      window.clearTimeout(handle)
    }
  }, [draft, q, setParam])

  const filteredEmpty = !loading && !error && listings.length === 0
  const hasFilters = Boolean(q || type || country)

  return (
    <main
      id="main"
      className="max-w-site px-margin-mobile md:px-margin-desktop mx-auto w-full flex-grow space-y-8 pt-24 pb-24"
    >
      <div className="space-y-3">
        <h1 className="text-headline-lg-mobile text-fg md:text-headline-lg">
          Browse live links
        </h1>
        <p className="font-body text-body-md text-fg-muted">
          Every link here is live and checked, and shows exactly how long it has
          left. See the brand and the offer up front — claim to open it.
        </p>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <label className="relative min-w-0 flex-1">
          <span className="sr-only">Search brands</span>
          <Icon
            icon={Search}
            className="text-fg-muted pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2"
          />
          <input
            type="search"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value)
            }}
            placeholder="Search brands"
            className="font-body text-body-md text-fg glass border-glass-border bg-surface-slate/40 placeholder:text-fg-muted focus:border-primary-container/50 w-full rounded-md border py-3 pr-4 pl-12 outline-none"
          />
        </label>

        <div className="relative z-20 md:w-56 md:shrink-0">
          <CountryField
            id="filter-country"
            label="Filter by country"
            labelHidden
            value={country ?? ''}
            onChange={(next) => {
              setParam('country', next || undefined)
            }}
          />
        </div>

        <div
          role="group"
          aria-label="Link type"
          className="flex flex-wrap gap-2"
        >
          {TYPE_FILTERS.map((filter) => {
            const selected = type === filter.value
            return (
              <button
                key={filter.label}
                type="button"
                aria-pressed={selected}
                onClick={() => {
                  setParam('type', filter.value)
                }}
                className={cn(
                  'font-body text-label-caps min-h-11 rounded-full border px-4 uppercase transition-colors',
                  selected
                    ? 'border-primary-container/30 bg-primary-container/15 text-primary'
                    : 'border-glass-border text-fg-muted hover:text-fg bg-white/5',
                )}
              >
                {filter.label}
              </button>
            )
          })}
        </div>
      </div>

      {loading && (
        <p className="font-body text-body-md text-fg-muted">Loading links…</p>
      )}
      {error && (
        <p className="font-body text-body-md text-error" role="alert">
          {error}
        </p>
      )}
      {usingSamples && listings.length > 0 && (
        <p className="font-body text-body-md text-fg-muted">
          Examples, while the feed fills up.{' '}
          <Link
            to="/drop"
            className="text-primary hover:text-primary-fixed-dim underline underline-offset-2"
          >
            Post a link
          </Link>{' '}
          and yours takes their place.
        </p>
      )}
      {filteredEmpty && !usingSamples && (
        <p className="font-body text-body-md text-fg-muted">
          {hasFilters ? (
            'No links match those filters.'
          ) : (
            <>
              No live links yet.{' '}
              <Link
                to="/drop"
                className="text-primary hover:text-primary-fixed-dim underline underline-offset-2"
              >
                Post the first one
              </Link>
              .
            </>
          )}
        </p>
      )}
      {usingSamples && listings.length === 0 && !loading && (
        <p className="font-body text-body-md text-fg-muted">
          No examples match those filters.
        </p>
      )}
      {!loading && listings.length > 0 && (
        <DropGrid listings={listings} sample={usingSamples} />
      )}
    </main>
  )
}
